import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { ArrowRight, Gift, Sparkles, CheckCircle2, XCircle, Timer } from 'lucide-react';
import { api } from '../api';

// Load 5 free challenges (1 per category, different for each visitor)
export const useFreeChallenges = () => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    let alive = true;
    api.funnelChallenges().then((r) => { if (alive) setItems(r.challenges || []); }).catch(() => alive && setItems([]));
    return () => { alive = false; };
  }, []);
  return items;
};

export const ChallengesLoading = () => (
  <div className="p-8 text-center text-sm text-[#B8A990]" data-testid="free-challenges-loading">
    <div className="mx-auto h-8 w-8 rounded-full border-2 border-[#F4C430] border-t-transparent animate-spin" />
    <div className="mt-3">Preparing your 5 brain challenges…</div>
  </div>
);

// -------- Step 2: Challenges (3 puzzles in-container) -----------------------
export const StepChallenges = ({ challenges, onDone }) => {
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [status, setStatus] = useState('idle');
  const [results, setResults] = useState([]);
  const puzzle = challenges[idx];
  const [seconds, setSeconds] = useState(puzzle.seconds);
  const timeoutRef = useRef(null);

  const isMemory = !!puzzle.memorizeSeconds;
  const [studyLeft, setStudyLeft] = useState(puzzle.memorizeSeconds || 0);
  useLayoutEffect(() => {
    setSelected(null); setStatus('idle');
    setSeconds(challenges[idx].seconds);
    setStudyLeft(challenges[idx].memorizeSeconds || 0);
  }, [idx, challenges]);

  // Memory: show the sequence for a few seconds, then hide it and ask the question
  const studying = isMemory && studyLeft > 0;
  useEffect(() => {
    if (!studying) return undefined;
    const t = setTimeout(() => setStudyLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [studying, studyLeft]);

  const timerVisible = idx >= 1 && !studying; // hide timer on first, show from 2nd on

  useEffect(() => {
    if (status !== 'idle' || !timerVisible) return;
    if (seconds <= 0) { setStatus('timeup'); return; }
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds, status, timerVisible]);

  useEffect(() => {
    if (status === 'idle') return;
    const wasCorrect = status === 'correct';
    const nextResults = [...results, { category: puzzle.category, correct: wasCorrect }];
    setResults(nextResults);
    timeoutRef.current = setTimeout(() => {
      if (idx < challenges.length - 1) setIdx(idx + 1);
      else onDone(nextResults);
    }, 1000);
    return () => timeoutRef.current && clearTimeout(timeoutRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const choose = (opt) => {
    if (status !== 'idle') return;
    setSelected(opt);
    setStatus(opt === puzzle.answer ? 'correct' : 'wrong');
  };

  return (
    <div className="p-6 sm:p-7 pt-4" data-testid="funnel-step-challenge">
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-2 rounded-full bg-[#3D2817] border border-[#4a3221] px-3 py-1 text-[11px] text-[#F4C430]">
          {puzzle.category} · Challenge {idx + 1} of {challenges.length}
        </div>
        {timerVisible && (
          <div className="text-[11px] text-[#B8A990] inline-flex items-center gap-1"><Timer size={12} /> 00:{String(Math.max(seconds, 0)).padStart(2, '0')}</div>
        )}
      </div>

      {studying ? (
        <div className="mt-4" data-testid="funnel-memory-study">
          <div className="text-sm text-[#B8A990]">{puzzle.studyText} — they'll disappear in <span className="text-[#F4C430] font-semibold" data-testid="funnel-memory-countdown">{studyLeft}s</span></div>
          <p className="mt-3 text-2xl sm:text-3xl text-[#F5EDE0] font-serif leading-snug">{puzzle.prompt}</p>
          <div className="mt-5 h-1.5 rounded-full bg-[#3D2817] overflow-hidden">
            <div className="h-full bg-[#F4C430] transition-[width] duration-1000 ease-linear" style={{ width: `${(studyLeft / puzzle.memorizeSeconds) * 100}%` }} />
          </div>
          <button type="button" onClick={() => setStudyLeft(0)} data-testid="funnel-memory-ready"
            className="mt-4 w-full rounded-full border border-[#5a3d24] h-11 text-sm text-[#F5EDE0] hover:border-[#F4C430] hover:text-[#F4C430]">
            I've memorised it
          </button>
        </div>
      ) : (
      <>
      <h3 className="mt-4 text-[#F5EDE0] text-base font-serif" data-testid="funnel-challenge-question">{puzzle.question}</h3>
      {!isMemory && <p className="mt-2 text-2xl sm:text-3xl text-[#F5EDE0] font-serif">{puzzle.prompt}</p>}

      <div className="mt-5 grid grid-cols-2 gap-2.5" data-testid="funnel-challenge-options">
        {puzzle.options.map((opt) => {
          const isSel = selected === opt;
          const isRight = status !== 'idle' && opt === puzzle.answer;
          const isWrongPick = status === 'wrong' && isSel;
          return (
            <button
              key={opt}
              onClick={() => choose(opt)}
              disabled={status !== 'idle'}
              data-testid={`funnel-challenge-opt-${opt}`}
              className={`rounded-xl border h-12 text-base text-[#F5EDE0] transition-all
                ${isRight ? 'bg-[#F4C430] text-[#2A1810] border-[#F4C430]' : ''}
                ${isWrongPick ? 'border-red-400 text-red-300' : ''}
                ${status === 'idle' ? 'border-[#5a3d24] hover:border-[#F4C430] hover:bg-[#4a3221] active:scale-[0.98]' : 'border-[#5a3d24]'}
              `}
            >
              {opt}
            </button>
          );
        })}
      </div>

      <div className="mt-3 min-h-[24px] text-xs sm:text-sm">
        {status === 'correct' && (<span className="inline-flex items-center gap-1.5 text-[#F4C430]"><CheckCircle2 size={16} /> Nice! Moving on…</span>)}
        {status === 'wrong'   && (<span className="inline-flex items-center gap-1.5 text-red-300"><XCircle size={16} /> Not quite — keep going</span>)}
        {status === 'timeup'  && (<span className="inline-flex items-center gap-1.5 text-[#E8B923]"><Timer size={16} /> Time up — next question</span>)}
        {status === 'idle'    && (<span className="text-[#B8A990]">Instant feedback · we track how you think</span>)}
      </div>
      </>
      )}
    </div>
  );
};

// -------- Step 3: Brain Report ---------------------------------------------
export const StepReport = ({ results, onContinue, ctaLabel = 'Continue', note = null }) => {
  const correct = results.filter((r) => r.correct).length;
  const total = Math.max(1, results.length);
  const score = Math.round((correct / total) * 100);

  return (
    <div className="p-6 sm:p-7 pt-4" data-testid="funnel-step-report">
      <div className="inline-flex items-center gap-2 rounded-full bg-[#3D2817] border border-[#F4C430]/40 px-3 py-1 text-[11px] text-[#F4C430]">
        <Sparkles size={12} /> Your Brain Report Is Ready!
      </div>
      <h2 className="mt-4 font-serif text-2xl sm:text-3xl leading-tight">
        Discover how your <span className="italic text-[#F4C430]">brain thinks</span>.
      </h2>
      <p className="mt-2 text-[#B8A990] text-sm">
        You got <span className="text-[#F5EDE0] font-semibold">{correct}/{results.length}</span> right — here's a snapshot of your cognitive strengths.
      </p>

      {/* Score card */}
      <div className="mt-5 rounded-2xl bg-[#3D2817] border border-[#4a3221] p-4 flex items-center gap-4">
        <div className="relative h-20 w-20 shrink-0">
          <svg viewBox="0 0 36 36" className="h-20 w-20 -rotate-90">
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#4a3221" strokeWidth="3" />
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#F4C430" strokeWidth="3"
              strokeDasharray={`${score}, 100`} strokeLinecap="round" />
          </svg>
          <div className="absolute inset-0 grid place-items-center">
            <div className="font-serif text-2xl text-[#F5EDE0]">{score}</div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-[#B8A990] uppercase tracking-widest">Brain Snapshot</div>
          <div className="mt-0.5 text-[#F5EDE0] font-medium">{score >= 66 ? 'Sharp thinker' : score >= 33 ? 'Curious explorer' : 'Warming up'}</div>
          <div className="mt-0.5 text-xs text-[#B8A990]">Save your results and unlock your full report</div>
        </div>
      </div>

      {/* Per-category breakdown */}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="funnel-report-breakdown">
        {results.map((r, i) => (
          <div key={i} className="rounded-xl border border-[#4a3221] bg-[#3D2817] p-2.5 text-center">
            <div className="text-[10px] uppercase tracking-widest text-[#B8A990]">{r.category}</div>
            <div className={`mt-1 text-sm font-semibold ${r.correct ? 'text-[#F4C430]' : 'text-red-300'}`}>
              {r.correct ? '✓ Correct' : '✕ Missed'}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 rounded-xl bg-[#3D2817] border border-[#F4C430]/40 p-3 flex items-start gap-2.5">
        <Gift size={14} className="text-[#F4C430] mt-0.5 shrink-0" />
        <div className="text-xs text-[#B8A990]">
          {note || <><span className="text-[#F5EDE0] font-medium">Every paid plan starts with a 7-day free trial</span> — or keep playing free with 5 puzzles a day.</>}
        </div>
      </div>

      <Button
        onClick={onContinue}
        data-testid="funnel-report-continue"
        className="mt-4 w-full rounded-full bg-[#F4C430] hover:bg-[#FFD34E] text-[#2A1810] font-semibold h-12"
      >
        {ctaLabel} <ArrowRight size={16} className="ml-1" />
      </Button>
    </div>
  );
};

